import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { GetTasksReportQuery } from '@confluence/tasks-report-macro/entry-points/GetTasksReportQuery.ts';
import type {
	GetTasksReportQuery$data,
	GetTasksReportQuery$variables,
} from '@confluence/tasks-report-macro/entry-points/GetTasksReportQueryTypes.ts';
import {
	parseMacroParams,
	type RawMacroParams,
} from '@confluence/tasks-report-macro/entry-points/buildTasksReportQueryVariables.ts';
import {
	buildTaskQueryMetadata,
	toLongIds,
	parseCommaSeparatedValues,
	CURRENT_SPACE_TOKEN,
	SELF_SPACE_TOKEN,
} from '@confluence/tasks-report-macro/entry-points/tasksReportQueryUtils.ts';

import type {
	MacroContentNode,
	MacroPreloadHandler,
	MacroPreloadContext,
} from '../preloadMacroData.ts';

/**
 * Splits the spaces macro param into two buckets at preload time, mirroring
 * the runtime hook's logic so the Apollo cache key matches exactly:
 *
 * - spaceIds: tokens (@self, currentSpace()) and numeric IDs resolved
 *   synchronously. At preload time there is no current-space context so tokens
 *   are dropped (toLongIds with undefined currentSpaceId skips them).
 * - spaceKeys: human-readable keys (e.g. "ENG") passed directly to the AGG
 *   query for server-side resolution (InlineTasksByMetadata.spaceKeys).
 *
 * Falls back to the context spaceKey when no explicit spaces are provided,
 * mirroring the "current page's space" default used at runtime.
 */
const resolveSpaceFilter = (
	spacesParam: string | undefined,
	contextSpaceKey: string,
): { spaceIds: number[]; spaceKeys: string[] } => {
	if (spacesParam) {
		const parts = parseCommaSeparatedValues(spacesParam) ?? [];
		const spaceIds = toLongIds(parts, undefined);
		const spaceKeys = parts.filter(
			(value) =>
				value !== CURRENT_SPACE_TOKEN &&
				value !== SELF_SPACE_TOKEN &&
				!/^\d+$/.test(value.startsWith('~') ? value.slice(1) : value),
		);

		if (spaceIds.length || spaceKeys.length) {
			return { spaceIds, spaceKeys };
		}
	}

	// Fall back to the current space from context when no explicit spaces provided.
	const contextSpaceId = Number.parseInt(contextSpaceKey, 10);
	if (!Number.isNaN(contextSpaceId)) {
		return { spaceIds: [contextSpaceId], spaceKeys: [] };
	}

	return { spaceIds: [], spaceKeys: [] };
};

/**
 * Preload handler for the tasks-report macro.
 *
 * Fires the GetTasksReportQuery GraphQL query to populate the store before the
 * macro renders on the client, eliminating a network waterfall.
 */
export const tasksReportPreloadHandler: MacroPreloadHandler = {
	extensionKey: 'tasks-report-macro',

	routes: ['view', 'space-overview', 'edit', 'live'],

	preload: async (node: MacroContentNode, context: MacroPreloadContext): Promise<void> => {
		try {
			const { cloudId } = getPreloaderFnContext();
			if (!cloudId) {
				return;
			}

			const macroParams = node.attrs?.parameters?.macroParams as RawMacroParams;
			const params = parseMacroParams(macroParams);

			const { spaceIds, spaceKeys } = resolveSpaceFilter(params.spaces, context.spaceKey);
			if (!spaceIds.length && !spaceKeys.length) {
				return;
			}

			// Keep preload variables identical to runtime so Apollo cache keys match.
			const taskQuery = buildTaskQueryMetadata(params, spaceIds, spaceKeys);

			await slowQueryGuard(
				'tasksReportPreload',
				() =>
					query<GetTasksReportQuery$data, GetTasksReportQuery$variables>({
						query: GetTasksReportQuery,
						variables: { taskQuery, cloudId },
						context: { single: true },
					}),
				SLOW_QUERY_DEFAULT_TIMEOUT,
			);
		} catch {
			return;
		}
	},
};
